import React, { useEffect, useState, useMemo, useRef } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import {
  AiOutlineClose,
  AiOutlineSearch,
  AiOutlineEdit,
  AiOutlineEye,
} from "react-icons/ai";
import SummaryApi from "../common";
import { useTable, usePagination } from "react-table";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFileExcel } from "react-icons/fa";

Modal.setAppElement("#root");

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: "rgb(23, 44, 87)",
  primaryLight: "rgba(23, 44, 87, 0.08)",
  actionBlue: "#2563EB",
  actionBlueHover: "#1D4ED8",
  actionYellow: "#D97706",
  actionYellowHover: "#B45309",
  actionGreen: "#10B981",
  actionGreenHover: "#059669",
  actionRed: "#EF4444",
  actionRedHover: "#DC2626",
};

// Columns that stay pinned to the right
const STICKY_ACCESSORS = ["edit", "view"];

const formatDateForAPI = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const DigitalPromotionLeadsReport = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [dropdownValue, setDropdownValue] = useState("");
  const [minEnrolledDate, setMinEnrolledDate] = useState(null);
  const [maxEnrolledDate, setMaxEnrolledDate] = useState(new Date());
  const [totalStudents, setTotalStudents] = useState(0);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [memberType, setMemberType] = useState(null);
  const [localPageIndex, setLocalPageIndex] = useState(0);
  const [updatedData, setUpdatedData] = useState({});
  const [updatedStudent, setUpdatedStudent] = useState(0);
  const [timer, setTimer] = useState("00:00:00");
  const [intervalId, setIntervalId] = useState(null);
  const [canOnlySelectHigherInterest, setCanOnlySelectHigherInterest] = useState(false);
  const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
  const [searchDistrict, setSearchDistrict] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [searchCollege, setSearchCollege] = useState("");
  const [selectedRange, setSelectedRange] = useState("");
  const [isCustomRange, setIsCustomRange] = useState(false);

  const [formData, setFormData] = useState({
    student_name: "",
    gender: "Male",
    father_name: "",
    mobile_number1: "",
    mobile_number2: "",
    email: "",
    aadhar_number: "",
    state: "",
    district: "",
    city: "",
    address: "",
    pincode: "",
    course: "",
    clg_name: "",
    program_type: "",
    ug_pg_course: "",
    call_back_reason: "",
    call_duration: "00:00:00.000000",
    other_reasons: "",
    is_interested: "",
    call_status: "",
    remarks: "",
    prev_call_back_reason: "",
    follow_up_date: "",
    previous_status: "",
    inter_percentage: "",
    is_jee: "",
    is_jee2: "",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  const districts = useMemo(
    () => [
      "Alluri Sitharama Raju", "Anakapalli", "Ananthapuram", "Annamayya",
      "Bapatla", "CHITTOOR", "EAST GODAVARI", "Eluru", "Guntur", "Kakinada",
      "Konaseema", "Krishna", "Kurnool", "Manyam", "Nandyal", "NTR", "Palnadu",
      "Prakasam", "Srikakulam", "SPS Nellore", "Sri Satyasai", "Tirupathi",
      "Vizianagaram", "Visakhapatnam", "West Godavari", "YSR Kadapa",
    ],
    [],
  );

  const reasonsForOthers = [
    "Wrong Number", "Not Reachable", "Number Not Working", "Call Declined",
    "Busy", "Switched Off", "Incoming Calls Stopped",
  ];

  const headers = {
    Authorization: "Bearer sandeep_university_916",
    "Content-Type": "application/json",
  };

  const gotoPageRef = useRef(null);
  const studentsDataRef = useRef([]);
  const memberTypeRef = useRef(null);
  const filteredStudentsRef = useRef([]);

  useEffect(() => { studentsDataRef.current = studentsData; }, [studentsData]);
  useEffect(() => { memberTypeRef.current = memberType; }, [memberType]);
  useEffect(() => { filteredStudentsRef.current = filteredStudents; }, [filteredStudents]);

  useEffect(() => {
    const today = new Date();
    const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    setFromDate(firstDayLastMonth);
    setToDate(today);
  }, []);

  const fetchSuggestions = useRef(
    debounce(async (query, overrideFrom = null, overrideTo = null) => {
      const latestMemberType = memberTypeRef.current;

      if (!query.trim() || !["Admin", "Manager", "Employee"].includes(latestMemberType)) {
        setSearchSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        let apiEndpoint = SummaryApi.digitalpromotionleadsreport.url;
        let requestBody = {
          search_term: query.trim(),
          start_date: formatDateForAPI(overrideFrom),
          end_date: formatDateForAPI(overrideTo),
        };

        if (latestMemberType === "Employee") {
          const encodedEmployeeId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
          if (encodedEmployeeId) {
            requestBody.employee_id = atob(encodedEmployeeId);
          } else {
            setSearchSuggestions([]);
            setShowSuggestions(false);
            return;
          }
        }

        const response = await fetch(apiEndpoint, {
          method: "POST",
          headers,
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();

        if (data.status && Array.isArray(data.data)) {
          const suggestions = data.data.map((student) => ({
            id: student.student_id,
            name: student.student_name === "N/A" ? "Unknown" : student.student_name,
            mobile: student.mobile_number1 === "N/A" ? "No Mobile" : student.mobile_number1,
            college: student.clg_name === "N/A" ? "Unknown College" : student.clg_name,
            employee:
              latestMemberType === "Admin" || latestMemberType === "Manager"
                ? student.employee_name || "N/A"
                : null,
          }));
          setSearchSuggestions(suggestions);
          setShowSuggestions(true);
        } else {
          setSearchSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (err) {
        console.error("Suggestions error:", err);
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }, 350),
  ).current;

  const getFormattedISTTime = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(now.getTime() + istOffset);
    const year = istTime.getUTCFullYear();
    const month = String(istTime.getUTCMonth() + 1).padStart(2, "0");
    const day = String(istTime.getUTCDate()).padStart(2, "0");
    const hours = String(istTime.getUTCHours()).padStart(2, "0");
    const minutes = String(istTime.getUTCMinutes()).padStart(2, "0");
    const seconds = String(istTime.getUTCSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  useEffect(() => {
    if (modalIsOpen && isUpdate) {
      let seconds = 0;
      const id = setInterval(() => {
        seconds++;
        const hours = String(Math.floor(seconds / 3600)).padStart(2, "0");
        const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
        const secs = String(seconds % 60).padStart(2, "0");
        setTimer(`${hours}:${minutes}:${secs}`);
      }, 1000);
      setIntervalId(id);
      return () => clearInterval(id);
    } else {
      if (intervalId) clearInterval(intervalId);
      setTimer("00:00:00");
    }
  }, [modalIsOpen, isUpdate]);

  const fetchStudents = async (skipLoading = false, overrideFrom = null, overrideTo = null) => {
    if (!skipLoading) setPageLoading(true);
    try {
      const encodedMemberType = sessionStorage.getItem("K3M8X1JQ5VZ4B7PWTL9");
      const type = encodedMemberType ? atob(encodedMemberType) : null;

      if (!type) {
        setStudentsData([]);
        setFilteredStudents([]);
        setTotalStudents(0);
        setPageLoading(false);
        return;
      }

      setMemberType(type);
      memberTypeRef.current = type;

      const effectiveFrom = overrideFrom !== null ? overrideFrom : fromDate;
      const effectiveTo = overrideTo !== null ? overrideTo : toDate;

      if (!effectiveFrom || !effectiveTo) return;

      const startDateFormatted = formatDateForAPI(effectiveFrom);
      const endDateFormatted = formatDateForAPI(effectiveTo);

      let response;

      if (type === "Admin" || type === "Manager") {
        response = await fetch(SummaryApi.digitalpromotionleadsreport.url, {
          method: SummaryApi.digitalpromotionleadsreport.method,
          headers: headers,
          body: JSON.stringify({
            start_date: startDateFormatted,
            end_date: endDateFormatted,
          }),
        });
      } else {
        const encodedEmployeeId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
        if (!encodedEmployeeId) {
          setStudentsData([]);
          setFilteredStudents([]);
          setTotalStudents(0);
          setPageLoading(false);
          return;
        }
        const decodedEmployeeId = atob(encodedEmployeeId);
        response = await fetch(SummaryApi.digitalpromotionleadsreport.url, {
          method: SummaryApi.digitalpromotionleadsreport.method,
          headers: headers,
          body: JSON.stringify({
            employee_id: decodedEmployeeId,
            start_date: startDateFormatted,
            end_date: endDateFormatted,
          }),
        });
      }

      if (!response?.ok) throw new Error("Failed to fetch data");

      const data = await response.json();
      const students = data?.data || [];

      const updatedStudentsData = students.map((student, index) => ({
        ...student,
        sno: index + 1,
        call_duration: student.call_duration
          ? student.call_duration.replace(/\.\d{6}$/, "")
          : student.call_duration,
      }));

      setTotalStudents(data?.total_students || students.length);
      setStudentsData(updatedStudentsData);
      setFilteredStudents(updatedStudentsData);
      setActiveSearchTerm("");
      setSearchQuery("");
      setShowSuggestions(false);

      if (gotoPageRef.current) gotoPageRef.current(0);
      setLocalPageIndex(0);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error(`Error fetching students: ${error.message}`);
      setStudentsData([]);
      setFilteredStudents([]);
      setTotalStudents(0);
    } finally {
      setPageLoading(false);
    }
  };

  const resetSearchStates = () => {
    setActiveSearchTerm("");
    setSearchQuery("");
    setShowSuggestions(false);
    setSearchSuggestions([]);
  };

  const applyAllFilters = (overrideSearchTerm = null) => {
    let filtered = [...studentsDataRef.current];

    const searchTerm = overrideSearchTerm !== null ? overrideSearchTerm : activeSearchTerm;

    if (searchTerm && searchTerm.trim().length >= 3) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((student) =>
        (student.student_name || "").toLowerCase().includes(term) ||
        (student.mobile_number1 || "").toLowerCase().includes(term) ||
        (student.clg_name || "").toLowerCase().includes(term)
      );
    }

    if (selectedFilter !== "All") {
      filtered = filtered.filter((student) => student.is_interested === selectedFilter);
    }

    if (selectedRange === "today") {
      const today = new Date().toDateString();
      filtered = filtered.filter((student) => new Date(student.created_date).toDateString() === today);
    } else if (selectedRange === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      filtered = filtered.filter((student) => new Date(student.created_date).toDateString() === yesterday.toDateString());
    } else if (selectedRange === "thisWeek") {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const d = new Date(student.created_date);
        return d >= startOfWeek && d <= endOfWeek;
      });
    } else if (selectedRange === "lastWeek") {
      const today = new Date();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      const endOfLastWeek = new Date(startOfCurrentWeek);
      endOfLastWeek.setDate(startOfCurrentWeek.getDate() - 1);
      endOfLastWeek.setHours(23, 59, 59, 999);
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      startOfLastWeek.setHours(0, 0, 0, 0);
      filtered = filtered.filter((student) => {
        const d = new Date(student.created_date);
        return d >= startOfLastWeek && d <= endOfLastWeek;
      });
    } else if (selectedRange === "custom" && fromDate && toDate) {
      const fromFormatted = new Date(fromDate);
      fromFormatted.setHours(0, 0, 0, 0);
      const toFormatted = new Date(toDate);
      toFormatted.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const d = new Date(student.created_date);
        return d >= fromFormatted && d <= toFormatted;
      });
    }

    setFilteredStudents(filtered);
    setTotalStudents(filtered.length);
    if (gotoPageRef.current) gotoPageRef.current(0);
    setLocalPageIndex(0);
  };

  const debouncedSearch = useRef(
    debounce(async (query) => {
      const latestStudents = studentsDataRef.current;

      if (!query.trim() || query.trim().length < 3) {
        setActiveSearchTerm("");
        applyAllFilters();
        return;
      }

      setIsSearching(true);
      setActiveSearchTerm(query);

      try {
        let searchResults = [...latestStudents];
        const searchTerm = query.trim().toLowerCase();

        searchResults = searchResults.filter((student) =>
          (student.student_name || "").toLowerCase().includes(searchTerm) ||
          (student.mobile_number1 || "").toLowerCase().includes(searchTerm) ||
          (student.clg_name || "").toLowerCase().includes(searchTerm)
        );

        if (selectedFilter !== "All") {
          searchResults = searchResults.filter((student) => student.is_interested === selectedFilter);
        }

        if (selectedRange === "today") {
          const today = new Date().toDateString();
          searchResults = searchResults.filter((student) => new Date(student.created_date).toDateString() === today);
        } else if (selectedRange === "yesterday") {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          searchResults = searchResults.filter((student) => new Date(student.created_date).toDateString() === yesterday.toDateString());
        } else if (selectedRange === "thisWeek") {
          const today = new Date();
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          endOfWeek.setHours(23, 59, 59, 999);
          searchResults = searchResults.filter((student) => {
            const d = new Date(student.created_date);
            return d >= startOfWeek && d <= endOfWeek;
          });
        } else if (selectedRange === "lastWeek") {
          const today = new Date();
          const startOfCurrentWeek = new Date(today);
          startOfCurrentWeek.setDate(today.getDate() - today.getDay());
          startOfCurrentWeek.setHours(0, 0, 0, 0);
          const endOfLastWeek = new Date(startOfCurrentWeek);
          endOfLastWeek.setDate(startOfCurrentWeek.getDate() - 1);
          endOfLastWeek.setHours(23, 59, 59, 999);
          const startOfLastWeek = new Date(endOfLastWeek);
          startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
          startOfLastWeek.setHours(0, 0, 0, 0);
          searchResults = searchResults.filter((student) => {
            const d = new Date(student.created_date);
            return d >= startOfLastWeek && d <= endOfLastWeek;
          });
        } else if (selectedRange === "custom" && fromDate && toDate) {
          const fromFormatted = new Date(fromDate);
          fromFormatted.setHours(0, 0, 0, 0);
          const toFormatted = new Date(toDate);
          toFormatted.setHours(23, 59, 59, 999);
          searchResults = searchResults.filter((student) => {
            const d = new Date(student.created_date);
            return d >= fromFormatted && d <= toFormatted;
          });
        }

        setFilteredStudents(searchResults);
        setTotalStudents(searchResults.length);
        if (gotoPageRef.current) gotoPageRef.current(0);
        setLocalPageIndex(0);
      } catch (err) {
        console.error("Search error:", err);
        toast.error("Search failed");
      } finally {
        setIsSearching(false);
      }
    }, 500),
  ).current;

  useEffect(() => {
    if (studentsDataRef.current.length > 0) {
      applyAllFilters();
    }
  }, [selectedFilter, selectedRange, fromDate, toDate, activeSearchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.length >= 3) {
      debouncedSearch(value);
      fetchSuggestions(value, fromDate, toDate);
    } else if (value.length === 0) {
      setActiveSearchTerm("");
      setShowSuggestions(false);
      setSearchSuggestions([]);
      applyAllFilters("");
    } else {
      setShowSuggestions(false);
      setSearchSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.name);
    setShowSuggestions(false);
    const result = filteredStudentsRef.current.filter((s) => s.student_id === suggestion.id);
    setFilteredStudents(result.length ? result : []);
    setTotalStudents(result.length);
  };

  useEffect(() => {
    return () => {
      if (debouncedSearch.cancel) debouncedSearch.cancel();
      if (fetchSuggestions.cancel) fetchSuggestions.cancel();
    };
  }, [debouncedSearch, fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filterStudentsByStatus = (filter, search, range) => {
    setLocalPageIndex(0);
    let filtered = [...studentsDataRef.current];
    const timeZone = "Asia/Kolkata";
    const formatToKolkata = (date) =>
      new Intl.DateTimeFormat("en-IN", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);

    if (filter !== "All") {
      filtered = filtered.filter((student) => student.is_interested === filter);
    }

    const convertToKolkataDateString = (date) => formatToKolkata(new Date(date));

    if (range === "today") {
      const todayFormatted = formatToKolkata(new Date());
      filtered = filtered.filter((student) => convertToKolkataDateString(student.created_date) === todayFormatted);
    } else if (range === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayFormatted = formatToKolkata(yesterday);
      filtered = filtered.filter((student) => convertToKolkataDateString(student.created_date) === yesterdayFormatted);
    } else if (range === "thisWeek") {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const d = new Date(student.created_date);
        return d >= startOfWeek && d <= endOfWeek;
      });
    } else if (range === "lastWeek") {
      const today = new Date();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      const endOfLastWeek = new Date(startOfCurrentWeek);
      endOfLastWeek.setDate(startOfCurrentWeek.getDate() - 1);
      endOfLastWeek.setHours(23, 59, 59, 999);
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      startOfLastWeek.setHours(0, 0, 0, 0);
      filtered = filtered.filter((student) => {
        const d = new Date(student.created_date);
        return d >= startOfLastWeek && d <= endOfLastWeek;
      });
    } else if (range === "custom") {
      if (!fromDate || !toDate) return;
      const fromFormatted = new Date(fromDate);
      fromFormatted.setHours(0, 0, 0, 0);
      const toFormatted = new Date(toDate);
      toFormatted.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const d = new Date(student.created_date);
        return d >= fromFormatted && d <= toFormatted;
      });
    }

    if (search) {
      filtered = filtered.filter(
        (student) =>
          (student.student_name || "").toLowerCase().includes(search.toLowerCase()) ||
          (student.mobile_number1 || "").toLowerCase().includes(search.toLowerCase()) ||
          (student.clg_name || "").toLowerCase().includes(search.toLowerCase()),
      );
    }

    setFilteredStudents(filtered);
    if (gotoPageRef.current) gotoPageRef.current(0);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);

    if (activeSearchTerm && activeSearchTerm.trim().length >= 3) {
      debouncedSearch(activeSearchTerm);
    } else {
      let filtered = [...studentsDataRef.current];

      if (filter !== "All") {
        filtered = filtered.filter((student) => student.is_interested === filter);
      }

      if (selectedRange === "today") {
        const today = new Date().toDateString();
        filtered = filtered.filter((student) => new Date(student.created_date).toDateString() === today);
      } else if (selectedRange === "yesterday") {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        filtered = filtered.filter((student) => new Date(student.created_date).toDateString() === yesterday.toDateString());
      } else if (selectedRange === "custom" && fromDate && toDate) {
        const fromFormatted = new Date(fromDate);
        fromFormatted.setHours(0, 0, 0, 0);
        const toFormatted = new Date(toDate);
        toFormatted.setHours(23, 59, 59, 999);
        filtered = filtered.filter((student) => {
          const d = new Date(student.created_date);
          return d >= fromFormatted && d <= toFormatted;
        });
      }

      setFilteredStudents(filtered);
      setTotalStudents(filtered.length);
    }

    if (gotoPageRef.current) gotoPageRef.current(0);
    setLocalPageIndex(0);
  };

  const handleRangeChange = (e) => {
    const range = e.target.value;
    setSelectedRange(range);
    setIsCustomRange(range === "custom");
    setActiveSearchTerm("");
    setSearchQuery("");
    setShowSuggestions(false);
    setSearchSuggestions([]);
    if (range !== "custom") {
      filterStudentsByStatus(selectedFilter, searchQuery, range);
    }
  };

  const clearFilters = () => {
    const today = new Date();
    const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    setFromDate(firstDayLastMonth);
    setToDate(today);
    setActiveSearchTerm("");
    setSearchQuery("");
    setSelectedFilter("All");
    setSelectedRange("");
    setIsCustomRange(false);
    setSearchSuggestions([]);
    setShowSuggestions(false);
    if (gotoPageRef.current) gotoPageRef.current(0);
    setLocalPageIndex(0);
    fetchStudents(false, firstDayLastMonth, today);
  };

  useEffect(() => {
    const init = async () => {
      const encodedMemberType = sessionStorage.getItem("K3M8X1JQ5VZ4B7PWTL9");
      const type = encodedMemberType ? atob(encodedMemberType) : null;
      setMemberType(type);
      memberTypeRef.current = type;

      if (fromDate && toDate) {
        await fetchStudents(false, fromDate, toDate);
      }
    };

    if (fromDate && toDate) {
      init();
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (memberType && fromDate && toDate && studentsData.length === 0) {
      fetchStudents(false, fromDate, toDate);
    }
  }, [memberType, fromDate, toDate]);

  useEffect(() => {
    if (studentsData?.length) {
      const dates = studentsData.map((student) => new Date(student.created_date));
      const minDate = new Date(Math.min(...dates));
      setMinEnrolledDate(minDate);
      setMaxEnrolledDate(new Date());
    }
  }, [studentsData]);

  const ugCourses = ["CSE", "CSE(AI & ML)", "CSE(AI & DS)", "AERONAUTICAL ENGINERRING", "ECE"];
  const pgCourses = [];
  const courses = formData.program_type === "UG" ? ugCourses : formData.program_type === "PG" ? pgCourses : [];

  const openModal = (student = null, mode = "view") => {
    const sanitizeStudentData = (data) => {
      const sanitizedData = { ...data };
      Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === "N/A" || sanitizedData[key] === "n/a") sanitizedData[key] = "";
      });
      return sanitizedData;
    };

    const payload = {
      employee_id: student?.employee_id,
      student_name: student?.student_name,
      gender: student?.gender,
      father_name: student?.father_name,
      mobile_number1: student?.mobile_number1,
      mobile_number2: student?.mobile_number2 || "",
      email: student?.email || "",
      aadhar_number: student?.aadhar_number || "",
      state: student?.state,
      district: student?.district,
      city: student?.city,
      address: student?.address,
      pincode: student?.pincode || "",
      course: student?.course,
      clg_name: student?.clg_name,
      inter_percentage: student?.inter_percentage || "",
      program_type: student?.program_type,
      ug_pg_course: student?.ug_pg_course,
      call_status: student?.call_status || "",
      call_duration: student?.call_duration || "00:00:00.000000",
      remarks: student?.remarks || "",
      is_interested: reasonsForOthers.includes(student?.is_interested) ? "Others" : student?.is_interested,
      other_reasons: reasonsForOthers.includes(student?.is_interested) ? student?.is_interested : "",
      call_back_reason: student?.call_back_reason,
      prev_call_back_reason: student?.call_back_reason,
      is_jee: student?.is_jee,
      is_jee2: student?.is_jee2,
    };

    const sanitizedStudent = sanitizeStudentData(payload);

    if (mode === "edit" && sanitizedStudent.is_interested === "Interested") {
      setCanOnlySelectHigherInterest(true);
    } else {
      setCanOnlySelectHigherInterest(false);
    }

    const [timePart = "00:00:00"] = sanitizedStudent.call_duration.split(".");
    const [hours, minutes, seconds] = timePart.split(":");

    setFormData({
      ...sanitizedStudent,
      hours: hours?.padStart(2, "0") || "00",
      minutes: minutes?.padStart(2, "0") || "00",
      seconds: seconds?.padStart(2, "0") || "00",
    });

    const coursesArray = sanitizedStudent.ug_pg_course
      ? sanitizedStudent.ug_pg_course.split(",").map((c) => c.trim())
      : [];
    setSelectedCourses(coursesArray);
    if (mode === "edit") setIsUpdate(true);

    setSelectedStudent(student);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedStudent(null);
    setIsUpdate(false);
    setSelectedCourses([]);
    setDropdownValue("");
    setCanOnlySelectHigherInterest(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "call_status" && (value === "Not Answered" || value === "")) {
      setFormData((prev) => ({ ...prev, is_interested: "", call_back_reason: "", other_reasons: "", [name]: value }));
    } else if (name === "is_interested") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(value !== "Call Back" && { call_back_reason: "" }),
        ...(value !== "Others" && { other_reasons: "" }),
      }));
    } else if (name === "program_type" && (value === "PG" || value === "")) {
      setSelectedCourses([]);
      setDropdownValue("");
      setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    }
  };

  const handleSelectChange = (e) => {
    const selectedCourse = e.target.value;
    if (selectedCourse && !selectedCourses.includes(selectedCourse)) {
      setSelectedCourses((prev) => [...prev, selectedCourse]);
    }
    setDropdownValue("");
  };

  const handleRemoveTag = (course) => {
    setSelectedCourses((prev) => prev.filter((c) => c !== course));
  };

  const validateForm = () => {
    const { call_status, is_interested, call_back_reason, other_reasons } = formData;
    if (call_status === "Contacted") {
      if (!["Not Interested", "Interested", "Most Interested", "Call Back", "Others"].includes(is_interested)) {
        toast.warning("Please select an option: Not Interested, Interested, Most Interested, Call Back, or Others.");
        return false;
      }
      if (is_interested === "Call Back" && !call_back_reason) {
        toast.warning("Please select a Call Back reason.");
        return false;
      }
      if (is_interested === "Others" && !other_reasons) {
        toast.warning("Please select an Other reason.");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    if (!validateForm()) { setLoadingSubmit(false); return; }

    try {
      const encodedEmployeeId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
      if (!encodedEmployeeId) throw new Error("Employee ID not found");
      const decodedEmployeeId = atob(encodedEmployeeId);
      const apiEndpoint = SummaryApi.updateStudent.url;
      const method = SummaryApi.updateStudent.method;

      let is_jee_value = "";
      if (formData.is_jee === "Jee Mains Results") {
        is_jee_value = "Jee Mains Results";
      } else if (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested)) {
        if (formData.prev_call_back_reason === "Jee Mains Results") is_jee_value = "Jee Mains Results";
      }

      let is_jee2_value = "";
      if (formData.is_jee2 === "Jee Mains-2 Results") {
        is_jee2_value = "Jee Mains-2 Results";
      } else if (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested)) {
        if (formData.prev_call_back_reason === "Jee Mains-2 Results") is_jee2_value = "Jee Mains-2 Results";
      }

      const payloadData = {
        employee_student_id: formData.employee_student_id || selectedStudent?.employee_student_id,
        student_name: formData.student_name,
        last_name: "",
        gender: formData.gender,
        father_name: formData.father_name,
        mobile_number1: formData.mobile_number1,
        mobile_number2: formData.mobile_number2 || "",
        email: formData.email || "",
        aadhar_number: formData.aadhar_number || "",
        state: formData.state,
        district: formData.district,
        city: formData.city,
        address: formData.address,
        pincode: formData.pincode || "",
        course: formData.course,
        clg_name: formData.clg_name,
        inter_percentage: formData.inter_percentage || "",
        program_type: formData.program_type,
        ug_pg_course: selectedCourses.join(", "),
        call_status: formData.call_status || "",
        call_duration: timer,
        remarks: formData.remarks || "",
        is_interested: formData.is_interested === "Others" ? formData.other_reasons : formData.is_interested,
        is_jee: is_jee_value,
        is_jee2: is_jee2_value,
        call_back_reason: formData.call_back_reason || "",
        follow_up_date: new Date().toISOString(),
      };

      const finalPayload = { ...payloadData, student_id: selectedStudent.student_id, employee_id: decodedEmployeeId };

      const response = await fetch(apiEndpoint, { method, headers, body: JSON.stringify(finalPayload) });
      if (!response.ok) throw new Error("Network response was not ok");

      const result = await response.json();
      if (result.status) {
        toast.success("Student updated successfully!");
        setUpdatedData(payloadData);
        setUpdatedStudent(selectedStudent?.student_id);
        setStudentsData((prev) => prev.map((s) => s.student_id === finalPayload.student_id ? { ...s, ...payloadData } : s));
        setFilteredStudents((prev) => prev.map((s) => s.student_id === finalPayload.student_id ? { ...s, ...payloadData } : s));
        closeModal();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const downloadExcelReport = () => {
    const requiredFields = [
      "student_id", "enrollment_code", "student_name", "email", "mobile_number1",
      "mobile_number2", "aadhar_number", "state", "district", "city", "address",
      "pincode", "course", "clg_name", "created_date", "call_duration", "call_status",
      "remarks", "employee_name", "is_interested", "call_back_reason", "other_reasons",
      "program_type", "ug_pg_course", "inter_percentage", "is_jee", "is_jee2", "follow_up_date",
    ];

    const headersMap = {
      student_id: "Student ID", enrollment_code: "Enrollment Code", student_name: "Student Name",
      email: "Email", mobile_number1: "Mobile Number 1", mobile_number2: "Mobile Number 2",
      aadhar_number: "Aadhar Number", state: "State", district: "District", city: "City",
      address: "Address", pincode: "Pincode", course: "Course", clg_name: "College",
      created_date: "Created Date", call_status: "Call Status", call_duration: "Call Duration",
      remarks: "Remarks", employee_name: "Employee Name", is_interested: "Interest Status",
      call_back_reason: "Call Back Reason", other_reasons: "Other Reasons",
      program_type: "Program Type", ug_pg_course: "UG/PG Course",
      inter_percentage: "Intermediate Percentage", is_jee: "JEE Status",
      is_jee2: "JEE 2 Status", follow_up_date: "Follow Up Date",
    };

    const formattedData = filteredStudents.map((student) => {
      const selectedData = {};
      requiredFields.forEach((field) => (selectedData[field] = student[field]));
      return Object.fromEntries(Object.entries(selectedData).map(([k, v]) => [headersMap[k] || k, v]));
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Digital_Leads_Report");
    XLSX.writeFile(workbook, "digital_promotion_leads_report.xlsx");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return "NA";
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const columns = useMemo(
    () => [
      { Header: "SNO", Cell: ({ row }) => row.index + 1 },
      ...(["Admin", "Manager"].includes(memberType) ? [{ Header: "Employee Name", accessor: "employee_name" }] : []),
      { Header: "Student Name", accessor: "student_name" },
      { Header: "District", accessor: "district" },
      { Header: "City", accessor: "city" },
      {
        Header: "College Name",
        accessor: "clg_name",
        Cell: ({ value }) => (
          <div className="group relative">
            <span>{value?.length > 15 ? `${value.slice(0, 15)}...` : value || "N/A"}</span>
            {value?.length > 15 && (
              <div className="absolute z-50 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap left-0 top-6 shadow-lg">
                {value}
              </div>
            )}
          </div>
        ),
      },
      {
        Header: "Address",
        accessor: "address",
        Cell: ({ value }) => {
          if (!value || value === "N/A" || value === "n/a") {
            return <span className="text-gray-400">NA</span>;
          }
          return (
            <div className="group relative max-w-[100px]">
              <span className="block truncate text-gray-700">{value}</span>
              <div className="absolute z-50 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 left-0 top-6 shadow-lg w-max max-w-[300px] whitespace-normal break-words pointer-events-none">
                {value}
              </div>
            </div>
          );
        },
      },
      { Header: "Mobile Number", accessor: "mobile_number1" },
      {
        Header: "Call Duration (HH:MM:SS)",
        accessor: "call_duration",
        Cell: ({ value }) => {
          if (value === "n/a" || !value) return "N/A";
          if (value.includes(":")) {
            const [timePart] = value.split(".");
            const [h, m, s] = timePart.split(":");
            return `${h.padStart(2, "0")}:${m.padStart(2, "0")}:${s.padStart(2, "0")}`;
          }
          return value;
        },
      },
      { Header: "Course", accessor: "course" },
      { Header: "Created Date", accessor: "created_date", Cell: ({ row }) => <span>{formatDate(row.original.created_date)}</span> },
      { Header: "Follow Up Date", accessor: "follow_up_date", Cell: ({ row }) => <span>{formatDate(row.original.follow_up_date)}</span> },
      {
        Header: "Current Status",
        accessor: "is_interested",
        Cell: ({ value }) => {
          if (!value) return <span>NA</span>;
          let colorClass = "text-gray-600";
          if (value === "Most Interested") colorClass = "text-green-600";
          else if (value === "Interested") colorClass = "text-yellow-500";
          else if (value === "Not Interested") colorClass = "text-red-500";
          return <span className={`font-semibold ${colorClass}`}>{value}</span>;
        },
      },
      ...(["Employee", "Consultant"].includes(memberType)
        ? [{
          Header: "Edit", accessor: "edit",
          Cell: ({ row }) => {
            const isDisabled = row.original.is_interested === "Most Interested";
            return (
              <button
                style={{
                  backgroundColor: isDisabled ? "#D1D5DB" : THEME.actionBlue,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!isDisabled) e.currentTarget.style.backgroundColor = THEME.actionBlueHover;
                }}
                onMouseLeave={(e) => {
                  if (!isDisabled) e.currentTarget.style.backgroundColor = THEME.actionBlue;
                }}
                className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                onClick={() => openModal(row.original, "edit")}
                disabled={isDisabled}
              >
                <AiOutlineEdit className="shrink-0" />
                <span>Edit</span>
              </button>
            );
          },
        }]
        : []),
      {
        Header: "View More", accessor: "view",
        Cell: ({ row }) => (
          <button
            style={{ backgroundColor: THEME.actionYellow }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellowHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellow)}
            className="text-white px-3 py-2 rounded-md transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
            onClick={() => openModal(row.original, "view")}
          >
            <AiOutlineEye className="shrink-0" />
            <span>View More</span>
          </button>
        ),
      },
    ],
    [memberType],
  );

  const {
    getTableProps, getTableBodyProps, headerGroups, prepareRow,
    page, canPreviousPage, canNextPage, gotoPage, nextPage, previousPage,
    setPageSize, pageCount,
    state: { pageIndex, pageSize },
  } = useTable({ columns, data: filteredStudents, initialState: { pageIndex: localPageIndex } }, usePagination);

  useEffect(() => { gotoPageRef.current = gotoPage; }, [gotoPage]);
  useEffect(() => { setLocalPageIndex(pageIndex); }, [pageIndex]);

  // Sticky cell width helper
  const getStickyWidth = (accessor) => {
    if (accessor === "edit") return 100;
    if (accessor === "view") return 150;
    return 120;
  };

  return (
    <div className="mx-auto">
      {pageLoading ? (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center">
          <span className="text-white text-lg">Fetching Data...</span>
        </div>
      ) : (
        <>
          <div className="mx-auto">
            {loadingSubmit && (
              <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center">
                <span className="text-white text-lg">Submitting...</span>
              </div>
            )}

            {/* Header row */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-6">
              <h1
                className="text-2xl md:text-3xl font-bold"
                style={{ color: THEME.primary }}
              >
                DM Leads Report
              </h1>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm md:text-base font-semibold">
                  <span style={{ color: THEME.actionRed }} className="font-bold">
                    {studentsData.length}
                  </span>
                  {" of "}
                  <span style={{ color: THEME.actionGreen }} className="font-bold">
                    {totalStudents}
                  </span>
                  {" Records Loaded"}
                </span>
                <button
                  onClick={() => {
                    setSearchModalIsOpen(true);
                    setSearchDistrict("");
                    setSearchCity("");
                    setSearchCollege("");
                  }}
                  style={{ backgroundColor: THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                  className="text-white px-5 py-2 rounded-lg shadow-sm text-sm font-semibold transition-opacity"
                >
                  Advanced Search
                </button>
              </div>
            </div>

            {/* Filters row */}
            <div>
              <div className="flex justify-between items-end flex-wrap gap-6 mb-6">
                <div className="flex flex-wrap gap-2">
                  {["All", "Interested", "Most Interested", "Not Interested", "Call Back"].map((f) => (
                    <button
                      key={f}
                      style={selectedFilter === f ? {} : { backgroundColor: THEME.primary }}
                      className={`py-2 px-4 rounded-lg text-white text-sm font-semibold transition-colors ${selectedFilter === f ? "bg-gray-700" : ""
                        }`}
                      onClick={() => handleFilterChange(f)}
                    >
                      {f}
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap gap-4 items-end">
                  {["Admin", "Manager", "Employee"].includes(memberType) && (
                    <>
                      <div>
                        <label
                          className="block text-sm font-semibold mb-1"
                          style={{ color: THEME.primary }}
                        >
                          From Date
                        </label>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => {
                            const newFromDate = date ? new Date(date) : null;
                            let effectiveTo = toDate;
                            if (newFromDate && toDate && newFromDate > toDate) {
                              effectiveTo = newFromDate;
                              setToDate(newFromDate);
                            }
                            setFromDate(newFromDate);
                            setActiveSearchTerm("");
                            setSearchQuery("");
                            setShowSuggestions(false);
                            if (gotoPageRef.current) gotoPageRef.current(0);
                            setLocalPageIndex(0);
                            fetchStudents(false, newFromDate, effectiveTo);
                          }}
                          selectsStart
                          startDate={fromDate}
                          endDate={toDate}
                          maxDate={toDate || maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="From"
                          className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                        />
                      </div>
                      <div>
                        <label
                          className="block text-sm font-semibold mb-1"
                          style={{ color: THEME.primary }}
                        >
                          To Date
                        </label>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => {
                            const newToDate = date ? new Date(date) : null;
                            setToDate(newToDate);
                            setActiveSearchTerm("");
                            setSearchQuery("");
                            setShowSuggestions(false);
                            if (gotoPageRef.current) gotoPageRef.current(0);
                            setLocalPageIndex(0);
                            fetchStudents(false, fromDate, newToDate);
                          }}
                          selectsEnd
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={fromDate}
                          maxDate={maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="To"
                          className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                        />
                      </div>
                      <div>
                        <label
                          className="block text-sm font-semibold mb-1"
                          style={{ color: THEME.primary }}
                        >
                          Date Range
                        </label>
                        <select
                          className="p-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                          style={{ color: THEME.primary }}
                          onChange={handleRangeChange}
                          value={selectedRange}
                        >
                          <option value="">All Time</option>
                          <option value="today">Today</option>
                          <option value="yesterday">Yesterday</option>
                          <option value="thisWeek">This Week</option>
                          <option value="lastWeek">Last Week</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={clearFilters}
                          style={{ backgroundColor: THEME.actionRed }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRedHover)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRed)}
                          className="px-5 py-2 text-white rounded-lg font-semibold text-sm transition-colors shadow-sm"
                        >
                          Clear Filters
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Search + Excel row */}
              <div className="relative flex justify-between mt-4 mb-6">
                <div className="w-full max-w-lg relative" ref={searchInputRef}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder={
                      ["Admin", "Manager"].includes(memberType)
                        ? "Search by student name, mobile number, or college name"
                        : "Search by student name, college name, or mobile number"
                    }
                    className="py-2.5 px-4 pr-12 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
                  />
                  <AiOutlineSearch className="absolute right-3 top-3 text-gray-400 text-xl" />
                </div>

                {["Admin", "Manager"].includes(memberType) && (
                  <button
                    style={{ backgroundColor: THEME.actionGreen }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreenHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreen)}
                    className={`flex items-center gap-2 text-white px-6 py-2.5 rounded-lg font-semibold transition-colors shadow-sm ${pageLoading ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    onClick={downloadExcelReport}
                    disabled={pageLoading}
                  >
                    <FaFileExcel size={18} />
                    Download Excel Report
                  </button>
                )}
              </div>

              {/* Table */}
              <div className="relative">
                <div className="overflow-x-auto shadow-sm rounded-lg border border-gray-200">
                  <table {...getTableProps()} className="min-w-full bg-white">
                    <thead className="bg-gray-200">
                      {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map(column => {
                            const isSticky = STICKY_ACCESSORS.includes(column.id) ||
                              STICKY_ACCESSORS.includes(column.accessor);
                            return (
                              <th
                                {...column.getHeaderProps()}
                                className={`px-4 py-2 text-left font-semibold whitespace-nowrap ${isSticky ? 'sticky right-0 z-20 bg-gray-200 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]' : ''}`}
                                style={{ color: THEME.primary }}
                              >
                                {column.render('Header')}
                              </th>
                            );
                          })}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row);
                        const stickyColsInOrder = row.cells.filter((cell) =>
                          STICKY_ACCESSORS.includes(cell.column.id) ||
                          STICKY_ACCESSORS.includes(cell.column.accessor)
                        );
                        return (
                          <tr
                            {...row.getRowProps()}
                            className="hover:bg-slate-50 transition duration-200"
                            key={row.index}
                          >
                            {row.cells.map((cell, i) => {
                              const isSticky =
                                STICKY_ACCESSORS.includes(cell.column.id) ||
                                STICKY_ACCESSORS.includes(cell.column.accessor);
                              if (!isSticky) {
                                return (
                                  <td {...cell.getCellProps()} className="p-3 border-b" key={i}>
                                    {cell.render("Cell")}
                                  </td>
                                );
                              }
                              const stickyIndexInRow = stickyColsInOrder.indexOf(cell);
                              const totalSticky = stickyColsInOrder.length;
                              const stickyWidths = stickyColsInOrder.map((c) =>
                                getStickyWidth(c.column.accessor || c.column.id)
                              );
                              let rightOffset = 0;
                              for (let k = stickyIndexInRow + 1; k < totalSticky; k++) {
                                rightOffset += stickyWidths[k];
                              }
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  className="p-3 border-b sticky z-10 bg-white shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]"
                                  style={{
                                    right: `${rightOffset}px`,
                                    backgroundColor: "#FFFFFF",
                                    minWidth: `${stickyWidths[stickyIndexInRow]}px`,
                                    width: `${stickyWidths[stickyIndexInRow]}px`,
                                  }}
                                  key={i}
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {(tableLoading || isSearching) && (
                  <div className="absolute inset-0 bg-gray-100/70 flex items-center justify-center z-10 rounded-lg">
                    <div className="flex items-center gap-3 bg-white px-8 py-5 rounded-xl shadow-lg">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#172C57]"></div>
                      <span className="text-gray-700 font-medium">{isSearching ? "Searching..." : "Loading..."}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4 flex-wrap gap-4">
                <span className="text-sm text-gray-700 font-medium">
                  Page {pageIndex + 1} of {pageCount} | Showing {filteredStudents.length} of {totalStudents} entries
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => gotoPage(0)}
                    disabled={!canPreviousPage}
                    style={{ color: THEME.primary }}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 font-semibold"
                  >
                    {"<<"}
                  </button>
                  <button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    style={{ color: THEME.primary }}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 font-semibold"
                  >
                    {"<"}
                  </button>
                  <button
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    style={{ color: THEME.primary }}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 font-semibold"
                  >
                    {">"}
                  </button>
                  <button
                    onClick={() => gotoPage(pageCount - 1)}
                    disabled={!canNextPage}
                    style={{ color: THEME.primary }}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 font-semibold"
                  >
                    {">>"}
                  </button>
                </div>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{ color: THEME.primary }}
                  className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>Show {size}</option>
                  ))}
                </select>
              </div>

              {filteredStudents.length === 0 && !pageLoading && !isSearching && (
                <div className="text-center py-8 text-gray-500">
                  No digital promotion leads found. Try adjusting your filters or search criteria.
                </div>
              )}
            </div>
          </div>

          {/* Edit/View Modal */}
          <Modal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            contentLabel="Student Details"
            className="mx-auto my-10 mt-[100px] max-h-[85vh] max-w-full sm:max-w-4xl bg-white rounded-lg shadow-lg overflow-auto"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50"
          >
            <div className="sticky top-0 z-10 bg-white px-8 py-4 border-b border-slate-100">
              <div className="flex justify-between items-center">
                <h2
                  className="text-2xl font-semibold"
                  style={{ color: THEME.primary }}
                >
                  {isUpdate ? "Edit Student" : "Student Details"}
                </h2>
                <button onClick={closeModal}>
                  <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800 transition duration-200" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 sm:p-8">
              <div className="flex flex-col">
                <label htmlFor="student_name" className="flex items-center text-sm font-semibold" style={{ color: THEME.primary }}>
                  Student Name<span className="ml-1 text-red-600">*</span>
                </label>
                <input type="text" id="student_name" name="student_name" value={formData.student_name} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} required />
              </div>

              <div className="flex flex-col">
                <label htmlFor="gender" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Gender</label>
                <select id="gender" name="gender" value={formData.gender} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex flex-col">
                <label htmlFor="father_name" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Father Name</label>
                <input type="text" id="father_name" name="father_name" value={formData.father_name} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} />
              </div>

              <div className="flex flex-col">
                <label htmlFor="mobile_number1" className="flex items-center text-sm font-semibold" style={{ color: THEME.primary }}>
                  Mobile Number 1<span className="ml-1 text-red-600">*</span>
                </label>
                <input type="tel" id="mobile_number1" name="mobile_number1" value={formData.mobile_number1}
                  onChange={(e) => { const v = e.target.value; if (/^\d*$/.test(v) && v.length <= 10) setFormData((p) => ({ ...p, mobile_number1: v })); }}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate}
                  required pattern="\d{10}" title="Mobile number must be exactly 10 digits." />
              </div>

              <div className="flex flex-col">
                <label htmlFor="mobile_number2" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Mobile Number 2</label>
                <input type="tel" id="mobile_number2" name="mobile_number2" value={formData.mobile_number2}
                  onChange={(e) => { const v = e.target.value; if (/^\d*$/.test(v) && v.length <= 10) setFormData((p) => ({ ...p, mobile_number2: v })); }}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate}
                  pattern="\d{10}" title="Mobile number must be exactly 10 digits." />
              </div>

              <div className="flex flex-col">
                <label htmlFor="email" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Email</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} />
              </div>

              <div className="flex flex-col">
                <label htmlFor="state" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>State</label>
                <input type="text" id="state" name="state" value={formData.state} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} />
              </div>

              <div className="flex flex-col">
                <label htmlFor="district" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>District</label>
                <select id="district" name="district" value={formData.district} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate}>
                  <option value="">Select District</option>
                  {districts.map((d, i) => <option key={i} value={d}>{d}</option>)}
                </select>
              </div>

              <div className="flex flex-col">
                <label htmlFor="city" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>City</label>
                <input type="text" id="city" name="city" value={formData.city} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} />
              </div>

              <div className="flex flex-col">
                <label htmlFor="clg_name" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>College Name</label>
                <input type="text" id="clg_name" name="clg_name" value={formData.clg_name} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} />
              </div>

              <div className="flex flex-col">
                <label htmlFor="address" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Address</label>
                <input type="text" id="address" name="address" value={formData.address} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} />
              </div>

              <div className="flex flex-col">
                <label htmlFor="pincode" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Pincode</label>
                <input type="text" id="pincode" name="pincode" value={formData.pincode}
                  onChange={(e) => { const v = e.target.value; if (/^\d*$/.test(v) && v.length <= 6) setFormData((p) => ({ ...p, pincode: v })); }}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate}
                  pattern="\d{6}" title="Pincode must be exactly 6 digits." />
              </div>

              <div className="flex flex-col">
                <label htmlFor="course" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Course</label>
                <input type="text" id="course" name="course" value={formData.course} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} />
              </div>

              <div className="flex flex-col">
                <label htmlFor="inter_percentage" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Intermediate Percentage/CGPA</label>
                <input type="text" id="inter_percentage" name="inter_percentage" value={formData.inter_percentage}
                  onChange={(e) => { const v = e.target.value; if (/^(\d+(\.\d*)?|\.\d+)?$/.test(v) || v === "") setFormData((p) => ({ ...p, inter_percentage: v })); }}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} />
              </div>

              <div className="flex flex-col">
                <label htmlFor="program_type" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Program Type</label>
                <select id="program_type" name="program_type" value={formData.program_type} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate}>
                  <option value="">Select Program Type</option>
                  <option value="UG">UG</option>
                  <option value="PG">PG</option>
                </select>
              </div>

              <div className="flex flex-col">
                <label htmlFor="ug_pg_course" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>UG/PG Course</label>
                <select id="ug_pg_course" name="ug_pg_course" value={dropdownValue} onChange={handleSelectChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate}>
                  <option value="">Select Course</option>
                  {courses.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedCourses.map((course) => (
                    <div
                      key={course}
                      className="flex items-center rounded-full px-3 py-1 border"
                      style={{
                        backgroundColor: THEME.primaryLight,
                        borderColor: "rgba(23, 44, 87, 0.2)",
                        color: THEME.primary,
                      }}
                    >
                      <span>{course}</span>
                      {isUpdate && (
                        <button type="button" className="ml-2" style={{ color: THEME.primary }} onClick={() => handleRemoveTag(course)}>
                          <AiOutlineClose className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col">
                <label htmlFor="call_duration" className="flex items-center text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Call Duration</label>
                <div className="flex flex-row space-x-2">
                  {[["hours", "h"], ["minutes", "m"], ["seconds", "s"]].map(([field, label], idx) => (
                    <React.Fragment key={field}>
                      {idx > 0 && <div className="flex items-center text-gray-500 font-bold">:</div>}
                      <div className="relative">
                        <input type="text" value={formData[field].padStart(2, "0")}
                          className="h-10 bg-gray-100 border border-gray-300 text-gray-900 rounded-md w-16 text-center shadow-sm focus:outline-none cursor-not-allowed" disabled />
                        <span className="absolute right-2 top-2 text-gray-500 font-bold">{label}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="flex flex-col col-span-1 sm:col-span-2">
                <label htmlFor="call_status" className="flex items-center text-sm font-semibold" style={{ color: THEME.primary }}>
                  Call Status<span className="ml-1 text-red-600">*</span>
                </label>
                <select id="call_status" name="call_status" value={formData.call_status} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${(!isUpdate || formData.call_status === "Contacted") && "cursor-not-allowed"}`}
                  disabled={formData.call_status === "Contacted" || !isUpdate} required>
                  <option value="">Select Status</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Not Answered">Not Answered</option>
                </select>
              </div>

              <div className="flex flex-col col-span-1 sm:col-span-2">
                <label htmlFor="remarks" className="text-sm font-semibold mb-1" style={{ color: THEME.primary }}>Remarks</label>
                <textarea id="remarks" name="remarks" value={formData.remarks} onChange={handleChange}
                  className={`p-2 bg-slate-100 border rounded ${!isUpdate && "cursor-not-allowed"}`} disabled={!isUpdate} rows={2} />
              </div>

              <div className="flex flex-col col-span-1 sm:col-span-2">
                <div className="flex flex-wrap gap-4">
                  {["Not Interested", "Interested", "Most Interested", "Call Back", "Others"].map((opt) => (
                    <label key={opt} className="flex items-center text-sm font-semibold" style={{ color: THEME.primary }}>
                      <input type="radio" name="is_interested" value={opt} checked={formData.is_interested === opt} onChange={handleChange}
                        className="mr-2"
                        disabled={formData.call_status === "Not Answered" || formData.call_status === "" || !isUpdate} />
                      {opt}
                    </label>
                  ))}
                </div>

                <div className="flex items-center mt-4">
                  {formData.is_interested === "Call Back" && (
                    <div className="flex items-center">
                      <label htmlFor="call_back_reason" className="mr-2 text-sm font-semibold" style={{ color: THEME.primary }}>
                        Call Back Reason<span className="text-red-600">*</span>:
                      </label>
                      <select id="call_back_reason" name="call_back_reason" value={formData.call_back_reason || ""} onChange={handleChange}
                        className="p-2 border border-gray-300 rounded" required={formData.is_interested === "Call Back"} disabled={!isUpdate}>
                        <option value="">Select Reason</option>
                        <option value="IPE Exams">IPE Exams</option>
                        <option value="Jee Mains Results">JEE Mains Results</option>
                        <option value="Jee Mains-2 Results">JEE Mains-2 Results</option>
                        <option value="Eamcet Results">EAMCET Results</option>
                        <option value="IPE Results">IPE Results</option>
                        <option value="Supply Exams">Supply Exams</option>
                        <option value="Supply Results">Supply Results</option>
                        <option value="EOD">EOD</option>
                      </select>
                    </div>
                  )}

                  {formData.is_interested === "Others" && (
                    <div className="flex items-center">
                      <label htmlFor="other_reasons" className="mr-2 text-sm font-semibold" style={{ color: THEME.primary }}>
                        Other Reason<span className="text-red-600">*</span>:
                      </label>
                      <select id="other_reasons" name="other_reasons" value={formData.other_reasons || ""} onChange={handleChange}
                        className="p-2 border border-gray-300 rounded" required={formData.is_interested === "Others"} disabled={!isUpdate}>
                        <option value="">Select Reason</option>
                        {reasonsForOthers.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {isUpdate && (
                <div className="flex flex-col col-span-1 sm:col-span-2">
                  <button
                    type="submit"
                    style={{ backgroundColor: THEME.primary }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                    className={`text-white px-4 py-2 rounded shadow-sm transition-opacity font-semibold ${loadingSubmit && "cursor-not-allowed opacity-60"}`}
                    disabled={loadingSubmit}
                  >
                    {loadingSubmit ? "Submitting..." : "Update Student"}
                  </button>
                </div>
              )}
            </form>
          </Modal>

          {/* Advanced Search Modal */}
          <Modal
            isOpen={searchModalIsOpen}
            onRequestClose={() => { setSearchModalIsOpen(false); setSearchDistrict(""); setSearchCity(""); setSearchCollege(""); }}
            contentLabel="Search Filters"
            className="mx-auto my-10 mt-[100px] max-w-md bg-white rounded-lg shadow-lg"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50"
          >
            <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold" style={{ color: THEME.primary }}>Search Filters</h2>
                <button onClick={() => setSearchModalIsOpen(false)}>
                  <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex flex-col">
                <label className="mb-1 font-semibold text-sm" style={{ color: THEME.primary }}>District</label>
                <select className="p-2 border rounded" value={searchDistrict}
                  onChange={(e) => { setSearchDistrict(e.target.value); setSearchCity(""); setSearchCollege(""); }}>
                  <option value="">Select District</option>
                  {Array.from(new Set(studentsData.map((s) => s.district))).filter(Boolean).map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="mb-1 font-semibold text-sm" style={{ color: THEME.primary }}>City</label>
                <select className="p-2 border rounded" value={searchCity} disabled={!searchDistrict}
                  onChange={(e) => { setSearchCity(e.target.value); setSearchCollege(""); }}>
                  <option value="">{searchDistrict ? "Select City" : "First select District"}</option>
                  {Array.from(new Set(studentsData.filter((s) => s.district?.toLowerCase() === searchDistrict.toLowerCase()).map((s) => s.city)))
                    .filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="mb-1 font-semibold text-sm" style={{ color: THEME.primary }}>College Name</label>
                <select className="p-2 border rounded" value={searchCollege} disabled={!searchCity}
                  onChange={(e) => setSearchCollege(e.target.value)}>
                  <option value="">{searchCity ? "Select College" : "First select City"}</option>
                  {Array.from(new Set(studentsData.filter((s) => s.city?.toLowerCase() === searchCity.toLowerCase()).map((s) => s.clg_name)))
                    .filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex justify-end pt-4">
                <button onClick={() => { setSearchDistrict(""); setSearchCity(""); setSearchCollege(""); setActiveSearchTerm(""); setSearchQuery(""); setShowSuggestions(false); applyAllFilters(); }}
                  className="px-4 py-2 mr-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Clear</button>
                <button
                  onClick={() => {
                    let filtered = [...studentsData];
                    if (searchDistrict) filtered = filtered.filter((s) => s.district?.toLowerCase() === searchDistrict.toLowerCase());
                    if (searchCity) filtered = filtered.filter((s) => s.city?.toLowerCase() === searchCity.toLowerCase());
                    if (searchCollege) filtered = filtered.filter((s) => s.clg_name?.toLowerCase().includes(searchCollege.toLowerCase()));
                    if (selectedFilter !== "All") filtered = filtered.filter((s) => s.is_interested === selectedFilter);
                    if (selectedRange === "today") {
                      const today = new Date().toDateString();
                      filtered = filtered.filter((s) => new Date(s.created_date).toDateString() === today);
                    } else if (selectedRange === "yesterday") {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      filtered = filtered.filter((s) => new Date(s.created_date).toDateString() === yesterday.toDateString());
                    } else if (selectedRange === "custom" && fromDate && toDate) {
                      const f = new Date(fromDate); f.setHours(0, 0, 0, 0);
                      const t = new Date(toDate); t.setHours(23, 59, 59, 999);
                      filtered = filtered.filter((s) => { const d = new Date(s.created_date); return d >= f && d <= t; });
                    }
                    setFilteredStudents(filtered);
                    setTotalStudents(filtered.length);
                    setSearchModalIsOpen(false);
                    if (gotoPageRef.current) gotoPageRef.current(0);
                    setLocalPageIndex(0);
                  }}
                  style={{ backgroundColor: THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                  className="px-4 py-2 text-white rounded transition-opacity">Apply</button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

function debounce(func, wait) {
  let timeout;
  const debounced = function (...args) {
    const later = () => { clearTimeout(timeout); func(...args); };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
  debounced.cancel = () => { clearTimeout(timeout); };
  return debounced;
}

export default DigitalPromotionLeadsReport;